// Contact tags. Keyed by NAME, not by id.
// Route: /settings/tags

import { useEffect, useState } from "react";

import { http, qs } from "../api";
import { useSession } from "../App";
import {
  Confirm,
  Dialog,
  DialogFooter,
  Empty,
  Field,
  Pager,
  RelativeTime,
  Search,
  Spinner,
  Table,
  Tag,
  useAsync,
  useToast,
  type Column,
} from "../ui";
import { PAGE_SIZE, errMessage, ErrorBanner, useDebounced } from "./shared";

// ============================================================ tags

export interface TagRow {
  name: string;
  color: string;
  created_at: string;
  updated_at: string;
}

export interface TagListPayload {
  tags?: TagRow[];
  total?: number;
}

const TAG_COLORS: { value: string; label: string; token: string }[] = [
  { value: "blue", label: "Blue", token: "var(--info)" },
  { value: "red", label: "Red", token: "var(--critical)" },
  { value: "green", label: "Green", token: "var(--success)" },
  { value: "yellow", label: "Yellow", token: "var(--warning)" },
  { value: "purple", label: "Purple", token: "var(--tag-purple)" },
  { value: "gray", label: "Gray", token: "var(--text-3)" },
];

function tagColorToken(color: string | undefined): string {
  return TAG_COLORS.find((c) => c.value === color)?.token ?? "var(--text-3)";
}

/** A tag chip carrying its colour as a leading dot, so the colour is never the only cue. */
export function TagChip({ name, color }: { name: string; color?: string }) {
  return (
    <span className="tag">
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: tagColorToken(color),
          display: "inline-block",
        }}
      />
      {name}
    </span>
  );
}

export function TagsView() {
  const { can } = useSession();
  const toast = useToast();
  const canWrite = can("tags", "write");
  const canDelete = can("tags", "delete");

  const [search, setSearch] = useState("");
  const query = useDebounced(search);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [query]);

  const { data, error, loading, reload } = useAsync<TagListPayload>(
    () => http.get<TagListPayload>(`/tags${qs({ search: query, page, limit: PAGE_SIZE })}`),
    [query, page]
  );
  const tags = data?.tags ?? [];
  const total = data?.total ?? tags.length;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<TagRow | null>(null);
  const [form, setForm] = useState({ name: "", color: "gray" });
  const [submitting, setSubmitting] = useState(false);
  const [toDelete, setToDelete] = useState<TagRow | null>(null);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", color: "gray" });
    setDialogOpen(true);
  };

  const openEdit = (tag: TagRow) => {
    setEditing(tag);
    setForm({ name: tag.name, color: tag.color || "gray" });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.push("Enter a tag name", "critical");
      return;
    }
    setSubmitting(true);
    try {
      if (editing) {
        // Tags are keyed by NAME, not by id, so the ORIGINAL name addresses the row.
        await http.put(`/tags/${encodeURIComponent(editing.name)}`, form);
        toast.push("Tag updated", "good");
      } else {
        await http.post("/tags", form);
        toast.push("Tag created", "good");
      }
      setDialogOpen(false);
      reload();
    } catch (err) {
      toast.push(errMessage(err, "Could not save the tag"), "critical");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    if (!toDelete) return;
    try {
      await http.del(`/tags/${encodeURIComponent(toDelete.name)}`);
      toast.push("Tag deleted", "good");
      setToDelete(null);
      reload();
    } catch (err) {
      toast.push(errMessage(err, "Could not delete the tag"), "critical");
      throw err;
    }
  };

  const columns: Column<TagRow>[] = [
    {
      key: "name",
      header: "Tag",
      sortable: true,
      render: (t) => <TagChip name={t.name} color={t.color} />,
    },
    {
      key: "color",
      header: "Colour",
      sortable: true,
      render: (t) => (
        <span className="muted">
          {TAG_COLORS.find((c) => c.value === t.color)?.label ?? "Gray"}
        </span>
      ),
    },
    {
      key: "created_at",
      header: "Created",
      sortable: true,
      render: (t) => <RelativeTime value={t.created_at} />,
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (t) => (
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <button
            className="btn icon sm ghost"
            title="Edit"
            disabled={!canWrite}
            onClick={() => openEdit(t)}
          >
            ✎
          </button>
          <button
            className="btn icon sm ghost"
            title="Delete"
            disabled={!canDelete}
            onClick={() => setToDelete(t)}
          >
            ✕
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Tags</h1>
          <div className="sub">
            <a href="/settings">Settings</a> · labels agents put on a conversation.
          </div>
        </div>
        <div className="page-actions">
          <button className="btn primary" onClick={openCreate} disabled={!canWrite}>
            + Add tag
          </button>
        </div>
      </div>

      <ErrorBanner error={error} what="Could not load the tags" />

      <div className="card flush">
        <div className="card-head">
          <h3>Organisation tags</h3>
          <Search value={search} onChange={setSearch} placeholder="Search tags…" />
        </div>
        <div className="card-body">
          <Table
            columns={columns}
            rows={tags}
            loading={loading}
            empty={
              <Empty
                title={query ? "No matching tags" : "No tags yet"}
                body={
                  query
                    ? `Nothing matches "${query}".`
                    : "Add a tag so conversations can be grouped and filtered."
                }
                icon="⌗"
                action={
                  canWrite ? (
                    <button className="btn primary sm" onClick={openCreate}>
                      + Add tag
                    </button>
                  ) : undefined
                }
              />
            }
          />
        </div>
        <div className="card-foot">
          <Pager
            total={total}
            limit={PAGE_SIZE}
            offset={(page - 1) * PAGE_SIZE}
            itemName="tags"
            onChange={(offset) => setPage(Math.floor(offset / PAGE_SIZE) + 1)}
          />
        </div>
      </div>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? "Edit tag" : "Create tag"}
      >
        <Field label="Name" hint="50 characters at most." required>
          <input
            maxLength={50}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>
        <Field label="Colour">
          <select value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })}>
            {TAG_COLORS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </Field>
        <div className="section">
          <h4>Preview</h4>
          <TagChip name={form.name || "Tag preview"} color={form.color} />
        </div>
        <DialogFooter>
          <button className="btn" onClick={() => setDialogOpen(false)} disabled={submitting}>
            Cancel
          </button>
          <button className="btn primary" onClick={() => void save()} disabled={submitting}>
            {submitting && <Spinner label="Saving" />}
            {editing ? "Update" : "Create"}
          </button>
        </DialogFooter>
      </Dialog>

      <Confirm
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={() => void remove()}
        title="Delete this tag?"
        body={`"${toDelete?.name ?? ""}" will be removed from every contact that carries it.`}
        danger
      />
    </div>
  );
}
